// modules/reporteCliente/pages/ReporteClienteListadoPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, TextField, MenuItem, Typography, Alert,
  CircularProgress, Chip, Checkbox, FormControlLabel,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon     from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import { buildEquiposMap, extractList } from "../../requerimiento/utils/extract";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const SUCURSALES = ["Todos", "La Paz", "Cochabamba", "Santa Cruz"];

const MONEDAS = { 1: "Dólares", 2: "Bolivianos" };

// El monto_total de factura siempre se guarda en bolivianos (el equivalente en dólares
// se calcula por factura vía cambio/cambio_ref), así que el total sumado siempre es en Bs,
// sin importar la moneda nominal del servicio del cliente.
const formatMonto = (value) => {
  const num = Number(value || 0);
  return `Bs ${num.toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const EQUIPOS_LABELS = {
  modem: "Modem",
  chip: "Chip",
  radio: "Radio",
  bateria: "Bat.",
  varios1: "Var.1",
  varios2: "Var.2",
  placa: "Placa",
};

const EquiposResumen = ({ equipos }) => {
  const entries = Object.entries(equipos || {}).filter(([, cantidad]) => Number(cantidad) > 0);
  if (!entries.length) return <Typography variant="caption" color="text.secondary">Sin equipos</Typography>;

  return (
    <Box sx={{ display: "flex", gap: 0.4, justifyContent: "center", flexWrap: "wrap", maxWidth: 190, mx: "auto" }}>
      {entries.map(([tipo, cantidad]) => (
        <Chip
          key={tipo}
          size="small"
          variant="outlined"
          label={`${EQUIPOS_LABELS[tipo] || tipo}: ${cantidad}`}
          sx={{ height: 18, fontSize: 10.5, "& .MuiChip-label": { px: 0.6 } }}
        />
      ))}
    </Box>
  );
};

const ReporteClienteListadoPage = () => {
  const navigate = useNavigate();

  const [sucursal, setSucursal]     = useState("Todos");
  const [codServicio, setCodServicio] = useState("Todos");
  const [servicios, setServicios]   = useState([]);
  const [buscarTexto, setBuscarTexto] = useState("");
  const [buscar, setBuscar]         = useState("");
  const [soloConEquipos, setSoloConEquipos] = useState(false);
  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(30);
  const [total, setTotal]           = useState(0);
  const [lista, setLista]           = useState([]);
  const [equiposMap, setEquiposMap] = useState({});
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    axios.get("/api/clientes/catalogos")
      .then(({ data }) => {
        const raw = data?.data ?? data;
        setServicios(Array.isArray(raw?.servicios) ? raw.servicios : []);
      })
      .catch(() => {});
  }, []);

  const fetchLista = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        buscar,
        sucursal,
        cod_servicio: codServicio !== "Todos" ? codServicio : undefined,
        solo_equipos: soloConEquipos ? 1 : undefined,
        page: opts.page ?? page,
        limit: opts.pageSize ?? pageSize,
      };
      const [res, respEquipos] = await Promise.all([
        axios.get("/api/facturas/reporte/busqueda", { params }),
        axios.get("/api/cliente-servicio/equipos"),
      ]);
      const data = res.data?.data ?? res.data;
      setLista(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
      setPage(Number(data?.page || params.page));
      setPageSize(Number(data?.limit || params.limit));
      setEquiposMap(buildEquiposMap(extractList(respEquipos?.data)));
    } catch (e) {
      setError(e?.response?.data?.message || "Error al cargar el listado");
    } finally {
      setLoading(false);
    }
  }, [buscar, sucursal, codServicio, soloConEquipos, page, pageSize]);

  useEffect(() => {
    fetchLista({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucursal, codServicio, buscar, soloConEquipos]);

  const handleBuscar = () => setBuscar(buscarTexto.trim());

  const handleExportar = async () => {
    setExportando(true);
    setError(null);
    try {
      const params = {
        buscar,
        sucursal,
        cod_servicio: codServicio !== "Todos" ? codServicio : undefined,
        solo_equipos: soloConEquipos ? 1 : undefined,
      };
      const response = await axios.get("/api/facturas/reporte/exportar", {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "reporte_clientes.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al exportar el listado");
    } finally {
      setExportando(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Reporte de Clientes"
        subtitle="Listado general de servicios por sucursal"
        backTo="/dashboard"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <TextField
            select size="small" label="Sucursal"
            sx={{ ...compactFieldSx, width: 150 }}
            value={sucursal}
            onChange={(e) => setSucursal(e.target.value)}
          >
            {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
          </TextField>

          <TextField
            select size="small" label="Servicio"
            sx={{ ...compactFieldSx, width: 150 }}
            value={codServicio}
            onChange={(e) => setCodServicio(e.target.value)}
          >
            <MenuItem value="Todos" sx={{ fontSize: "0.85rem" }}>Todos</MenuItem>
            {servicios.map((sv) => (
              <MenuItem key={sv.id_servicio} value={sv.id_servicio} sx={{ fontSize: "0.85rem" }}>
                {sv.nombre}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Buscar razón social / descripción / grupo"
            value={buscarTexto}
            onChange={(e) => setBuscarTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleBuscar();
            }}
            sx={{ ...compactFieldSx, width: { xs: "100%", sm: 300 } }}
          />

          <Button
            size="small"
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleBuscar}
            sx={{ minHeight: 32 }}
          >
            Buscar
          </Button>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={soloConEquipos}
                onChange={(e) => setSoloConEquipos(e.target.checked)}
              />
            }
            label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Solo con equipos</Typography>}
            sx={{ ml: 0.5, mr: 0 }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            Total: {total}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={exportando ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExportar}
            disabled={exportando || total === 0}
            sx={{ minHeight: 32 }}
          >
            {exportando ? "Exportando..." : "Exportar Excel"}
          </Button>

          {loading && <CircularProgress size={20} />}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && lista.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No hay servicios para los filtros seleccionados.
          </Alert>
        ) : (
          <>
            <Paper variant="outlined">
              <TableContainer>
                <Table size="small" sx={{ "& td": { py: 0.25, px: 0.75, fontSize: "0.78rem" } }}>
                  <TableHead>
                    <TableRow>
                      <StyledHeaderCell>COD Servicio</StyledHeaderCell>
                      <StyledHeaderCell>COD Cliente</StyledHeaderCell>
                      <StyledHeaderCell>Razón Social</StyledHeaderCell>
                      <StyledHeaderCell>Servicio</StyledHeaderCell>
                      <StyledHeaderCell>Grupo</StyledHeaderCell>
                      <StyledHeaderCell>Tipo</StyledHeaderCell>
                      <StyledHeaderCell>Sucursal</StyledHeaderCell>
                      <StyledHeaderCell>Descripción</StyledHeaderCell>
                      <StyledHeaderCell>Fecha Reg. Cliente</StyledHeaderCell>
                      <StyledHeaderCell>Fecha Reg. Servicio</StyledHeaderCell>
                      <StyledHeaderCell align="center">Estado</StyledHeaderCell>
                      <StyledHeaderCell align="center">Moneda</StyledHeaderCell>
                      <StyledHeaderCell align="center">Equipos</StyledHeaderCell>
                      <StyledHeaderCell align="center">Factura</StyledHeaderCell>
                      <StyledHeaderCell align="right">Total Facturado (Bs)</StyledHeaderCell>
                      <StyledHeaderCell>Última Factura</StyledHeaderCell>
                      <StyledHeaderCell align="center">Acción</StyledHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={17} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      lista.map((s) => (
                        <TableRow key={s.id} hover>
                          <TableCell>{s.id}</TableCell>
                          <TableCell>{s.cod_cliente}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: "0.78rem", lineHeight: 1.25 }}>
                              {s.razon_social}
                            </Typography>
                          </TableCell>
                          <TableCell>{s.servicio_nombre}</TableCell>
                          <TableCell>{s.grupo || "-"}</TableCell>
                          <TableCell>{s.tipo}</TableCell>
                          <TableCell>{s.sucursal_facturar}</TableCell>
                          <TableCell>{s.descripcion}</TableCell>
                          <TableCell>{formatFecha(s.cliente_fecha_registro)}</TableCell>
                          <TableCell>{formatFecha(s.servicio_fecha_registro)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={s.estado || "-"}
                              size="small"
                              color={s.estado === "Activo" ? "success" : "default"}
                              sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="caption" color="text.secondary">
                              {MONEDAS[s.moneda] || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <EquiposResumen equipos={equiposMap[String(s.id)]} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={s.tiene_factura ? "Sí" : "No"}
                              size="small"
                              color={s.tiene_factura ? "success" : "default"}
                              sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.78rem" }}>
                              {formatMonto(s.total_facturado)}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatFecha(s.ultima_factura_fecha)}</TableCell>
                          <TableCell align="center">
                            <Button
                              size="small" variant="outlined"
                              sx={{ minHeight: 26, fontSize: "0.72rem", px: 0.75 }}
                              startIcon={<VisibilityIcon fontSize="small" />}
                              onClick={() => navigate(`/reporte-cliente/${s.id}`, { state: { from: "/reporte-cliente/listado" } })}
                            >
                              Ver reporte
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <TablePagination
              component="div"
              count={total}
              page={Math.max(page - 1, 0)}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[30, 50, 100, 200]}
              labelRowsPerPage="Limite"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              onPageChange={(_, newPage) => fetchLista({ page: newPage + 1 })}
              onRowsPerPageChange={(e) => fetchLista({ page: 1, pageSize: Number(e.target.value) })}
              sx={{
                minHeight: 36,
                "& .MuiTablePagination-toolbar": { minHeight: 36, pl: 1 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "0.78rem",
                },
              }}
            />
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteClienteListadoPage;
